// 4.10 The owner's weekly review: one card per prospect, the draft on the left and the research card on the right
// (stacked on a phone, research folded away). Approve, edit in place or reject with a one-tap reason, card by card;
// "Approve all like this" once three of the same angle and confidence are approved; progress; and keyboard
// shortcuts on a desktop (a approve, e edit, r reject, j next, k previous). The rules are in lib/review.ts.
// J-03 (18 Oct 2026), approve and send fast: one primary "Approve all N" at the top of the batch; each card shows
// the message and the draft coach's score only, and its "More" opens the coach's reasons and rewrites, Phone view
// and the research column (which is left out altogether when there is no research). Edit and Reject are quiet links.
// K-12 (Find walk-through, 19 Oct 2026): on Prospects › Drafts › To approve each card also says why this firm and
// why this message (from the research and scoring already recorded), and Delay sends the firm back to the Long list.
// 2.43 (28 Oct 2026): a reason-led draft shows its evidence under the message, outside More: Find's reason with its
// source, and the lines of the firm's brief it used, each linking its page (metadata.research.evidence).
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { Crm } from "../../lib/crm";
import type { HumanTask, OutboxItem, OutreachAssetPreview, RejectReason, ReviewDraft } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { fmtDateTime, humanise } from "../../lib/format";
import { Async, DangerIcon, Empty, Notice } from "../../components/ui";
import {
  BRIEF_FIELD_LABEL,
  CONFIDENCE_LABEL,
  REJECT_REASONS,
  approveAllLike,
  failedChecks,
  nextOpen,
  progressText,
  reasonLabel,
  researchOf,
  shortcutFor,
  type Decision,
  type ResearchCard,
  type ResearchEvidence,
} from "../../lib/review";
import { DraftCoach } from "../../components/approvals/DraftCoach";
import "../find/find-review.css";

export { REJECT_REASONS } from "../../lib/review";

type Edit = { subject: string; body: string };

const RULES: Record<string, string> = {
  all: "allowed to anyone",
  corporate_only: "allowed to companies, not to sole traders",
  consent_only: "only to people who have said yes to hearing from you",
  none: "not allowed",
};

/** "Email to firms in GB: allowed to companies, not to sole traders", from the business's compliance.by_country rules. */
export function ruleText(d: ReviewDraft): string | null {
  if (!d.compliance_rule) return null;
  const where = d.country ? `firms in ${d.country}` : "firms with no country recorded";
  return `${humanise(d.channel)} to ${where}: ${RULES[d.compliance_rule] ?? humanise(d.compliance_rule).toLowerCase()}`;
}

/** Whether a draft has anything for the research column: a research card or the message a reply answers. (The country
 *  rule alone is not research: it goes under the message, inside More.) */
export function hasResearch(d: Pick<ReviewDraft, "is_reply">, card: ResearchCard | null): boolean {
  return Boolean(card || d.is_reply);
}

/** Drafts "Approve all" may sweep up: not one the country rules now forbid, and none with a failed check. */
export function approvableAll(drafts: ReviewDraft[], decisions: Record<string, Decision>): ReviewDraft[] {
  return drafts.filter((d) => !decisions[d.id] && d.channel_allowed !== false && failedChecks(researchOf(d)).length === 0);
}

/** A WhatsApp template message (4.9 follow-ups, 5.9 opt-in requests, 7.7 reminders, 10.13 updates): Meta sends only
 *  the approved template with its values, so its text can't be edited here (review fix 9); approve it or reject it.
 *  Replies inside the window (6.13) have their own card and can be edited. */
export function isWhatsAppTemplate(d: Pick<OutboxItem, "channel" | "step_id">): boolean {
  return d.channel === "whatsapp" && d.step_id !== "6.13";
}

/** Only fields that differ from the draft go into p_edits, keyed by outbox id (never for a WhatsApp template). */
export function buildEdits(drafts: OutboxItem[], edits: Record<string, Edit>, ids: string[]): Record<string, { subject?: string; body?: string }> {
  const out: Record<string, { subject?: string; body?: string }> = {};
  for (const d of drafts) {
    if (!ids.includes(d.id) || isWhatsAppTemplate(d)) continue;
    const e = edits[d.id];
    if (!e) continue;
    const change: { subject?: string; body?: string } = {};
    if (e.subject !== (d.subject ?? "")) change.subject = e.subject;
    if (e.body !== d.body) change.body = e.body;
    if (Object.keys(change).length) out[d.id] = change;
  }
  return out;
}

/** The research card: who, the firm, why now, the hook, sources as links, confidence, and failed checks first. */
function Research({ card, d, open, onToggle }: { card: ResearchCard | null; d: ReviewDraft; open: boolean; onToggle: () => void }) {
  const rule = ruleText(d);
  const failed = failedChecks(card);
  const id = useId();
  return (
    <aside className={`research${open ? " is-open" : ""}`} aria-label="Research">
      <button type="button" className="research-toggle" aria-expanded={open} aria-controls={id} onClick={onToggle}>
        Research{card?.confidence ? ` · ${CONFIDENCE_LABEL[card.confidence].toLowerCase()}` : ""}
        {failed.length ? ` · ${failed.length} ${failed.length === 1 ? "check" : "checks"} failed` : ""}
      </button>
      <div className="research-body" id={id}>
        {failed.length > 0 && (
          <ul className="research-failed" aria-label="Checks that failed">
            {failed.map((c) => (
              <li key={c.rule}>
                <DangerIcon />
                <strong>{humanise(c.rule)}</strong>
                {c.detail ? `: ${c.detail}` : ""}
              </li>
            ))}
          </ul>
        )}
        {card ? (
          <>
            <div className="research-head">
              {card.confidence ? <span className={`confidence confidence-${card.confidence}`}>{CONFIDENCE_LABEL[card.confidence]}</span> : null}
              {d.angle ? <span className="og-tag">{d.angle_label ?? humanise(d.angle)}</span> : null}
            </div>
            <dl className="research-facts">
              {card.who && (
                <>
                  <dt>Who</dt>
                  <dd>{card.who}</dd>
                </>
              )}
              {card.firm && (
                <>
                  <dt>Firm</dt>
                  <dd>{card.firm}</dd>
                </>
              )}
              {card.why_now && (
                <>
                  <dt>Why now</dt>
                  <dd>{card.why_now}</dd>
                </>
              )}
              {card.hook && (
                <>
                  <dt>{card.legacy ? "Personal hook" : "Hook"}</dt>
                  <dd>{card.hook}</dd>
                </>
              )}
            </dl>
            {card.sources.length > 0 && (
              <p className="research-sources small">
                <span className="muted">Sources: </span>
                {card.sources.map((s, i) => (
                  <span key={`${s.label}-${i}`}>
                    {i > 0 ? ", " : ""}
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noreferrer noopener">
                        {s.label}
                      </a>
                    ) : (
                      <span className="muted">{s.label}</span>
                    )}
                  </span>
                ))}
              </p>
            )}
            {card.checks.some((c) => c.ok) && (
              <p className="small muted">
                Passed: {card.checks.filter((c) => c.ok).map((c) => humanise(c.rule).toLowerCase()).join(", ")}.
              </p>
            )}
          </>
        ) : (
          <p className="muted small">No research recorded for this draft.</p>
        )}
        {d.is_reply ? (
          // card-check: not a card (a show-more of the message being answered)
          <details className="disclosure">
            <summary>
              A reply to their message{d.reply_to_received_at ? ` of ${fmtDateTime(d.reply_to_received_at)}` : ""}
              {d.mailbox ? `, sent from ${d.mailbox} in the same thread` : ""}
            </summary>
            <blockquote className="message-body">{d.reply_to_body ?? "(message not found)"}</blockquote>
          </details>
        ) : (
          rule && <p className="muted small">{rule}.</p>
        )}
      </div>
    </aside>
  );
}

/** 2.43 Beside a reason-led draft: the reason it cites with its source, and the lines of the firm's brief it used. */
function Evidence({ e }: { e: ResearchEvidence }) {
  const source = e.reason.source ?? (e.reason.url ? new URL(e.reason.url).hostname.replace(/^www\./, "") : null);
  return (
    <div className="review-evidence small" aria-label="Evidence">
      <p>
        <span className="muted">Why now: </span>
        {e.reason.line}{" "}
        {source &&
          (e.reason.url ? (
            <a href={e.reason.url} target="_blank" rel="noreferrer noopener">
              {source}
            </a>
          ) : (
            <span className="muted">({source})</span>
          ))}
      </p>
      {e.brief.length > 0 && (
        <ul aria-label="From their website">
          {e.brief.map((b) => (
            <li key={b.field}>
              <span className="muted">{BRIEF_FIELD_LABEL[b.field] ?? humanise(b.field)}: </span>
              {b.text}
              {b.url && (
                <>
                  {" "}
                  <a href={b.url} target="_blank" rel="noreferrer noopener">
                    their page
                  </a>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ASSET_LABELS: Record<OutreachAssetPreview["kind"], string> = {
  website_preview: "Preview website",
  presence_report: "Online presence report",
  sector_checklist: "Sector checklist",
  benchmark: "Benchmark",
  library_piece: "Article from your library",
  estimate: "Estimate",
};

/** The banner the asset page adds, shown in the preview so the reviewer sees the page as the recipient will. */
export function assetPreviewHtml(a: OutreachAssetPreview, sender = "you"): string {
  const banner =
    '<div style="font:14px/1.4 system-ui,sans-serif;background:#1f2937;color:#fff;padding:10px 16px">' +
    `A free preview made by ${sender.replace(/[<>&"]/g, "")}. Not public, comes down 30 days after sending. Claim it or remove it.</div>`;
  return (a.html ?? "").replace("<!--ogma:banner-->", banner);
}

/** 4.16 The asset that goes with the message: what it is, its address once sent, and the page itself (sandboxed). */
function AssetPanel({ a }: { a: OutreachAssetPreview }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const address = `${a.slug}.ogmahub.com`;
  return (
    <div className="review-asset">
      <p className="small">
        <strong>Goes with: {ASSET_LABELS[a.kind] ?? humanise(a.kind)}</strong>{" "}
        <span className="muted">
          at {address}, live once this message is sent, for 30 days. Approving the message approves it; rejecting removes it.
        </span>
      </p>
      {a.html ? (
        <>
          <button type="button" className="btn btn-small" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
            {open ? "Hide the preview" : "Show the preview"}
          </button>
          {open && (
            <iframe
              id={id}
              className="asset-frame"
              title={`Preview of ${a.title ?? address}`}
              sandbox=""
              srcDoc={assetPreviewHtml(a)}
              style={{ width: "100%", height: 420, border: "1px solid var(--line, #ddd)", borderRadius: 8, marginTop: 8 }}
            />
          )}
        </>
      ) : (
        <p className="muted small">The page is not available to preview.</p>
      )}
    </div>
  );
}

/** Who a draft goes to, in a line ("Sarah Kemp, Harbour Print"). */
const draftWho = (d: ReviewDraft) => [d.person?.full_name, d.organisation?.name].filter(Boolean).join(", ") || "Unknown recipient";
/** When it goes ("Email, sends 1 Nov, 09:30 once approved"). */
const sendsText = (d: ReviewDraft) => `${humanise(d.channel)}${d.scheduled_for ? `, sends ${fmtDateTime(d.scheduled_for)} once approved` : ", sends once approved"}`;

/** A draft as one row (Campaigns › To approve, 31 Oct 2026): who, the subject or first line, when it goes, and Approve.
 * Clicking it opens the whole message in place. */
function ReviewRow({ d, edit, decision, busy, onOpen, onApprove }: { d: ReviewDraft; edit: Edit; decision: Decision | undefined; busy: boolean; onOpen: () => void; onApprove: () => void }) {
  const first = (edit.subject || edit.body.split("\n").find((l) => l.trim()) || "").trim();
  return (
    <li className={`review-row${decision ? ` is-${decision.kind}` : ""}`} data-draft={d.id}>
      <button type="button" className="review-row-open" aria-expanded="false" aria-label={`Open the message to ${draftWho(d)}`} onClick={onOpen}>
        <strong>{draftWho(d)}</strong>
        <span className="review-row-preview">{first}</span>
        <span className="muted small">{sendsText(d)}</span>
      </button>
      {decision ? (
        <span className="chip">{decision.kind === "approved" ? "Approved" : "Rejected"}</span>
      ) : (
        <button type="button" className="btn btn-small" disabled={busy} onClick={onApprove} aria-label={`Approve the message to ${draftWho(d)}`}>Approve</button>
      )}
    </li>
  );
}

function ReviewCard({
  d,
  edit,
  decision,
  current,
  editing,
  rejecting,
  busy,
  primary,
  researchOpen,
  like,
  onFocus,
  onEdit,
  onToggleEdit,
  onApprove,
  onStartReject,
  onReject,
  onApproveLike,
  onToggleResearch,
  why,
  onDelay,
  onClose,
}: {
  d: ReviewDraft;
  edit: Edit;
  decision: Decision | undefined;
  current: boolean;
  editing: boolean;
  rejecting: boolean;
  busy: boolean;
  /** The card's Approve is the page's main action (a batch of one, where there is no "Approve all"). */
  primary: boolean;
  researchOpen: boolean;
  like: ReturnType<typeof approveAllLike>;
  onFocus: () => void;
  onEdit: (e: Edit) => void;
  onToggleEdit: () => void;
  onApprove: () => void;
  onStartReject: () => void;
  onReject: (reason: RejectReason, note: string) => void;
  onApproveLike: () => void;
  onToggleResearch: () => void;
  /** K-12 a short "why this firm, why this message" under the recipient. */
  why?: ReactNode;
  /** K-12 Delay: the firm goes back to the Long list. */
  onDelay?: () => void;
  /** In the compact list, folds the message back to its row. */
  onClose?: () => void;
}) {
  const id = useId();
  const [note, setNote] = useState("");
  // A reply opens with More open: what it answers is part of the message to approve.
  const [more, setMore] = useState(Boolean(d.is_reply));
  const fixed = isWhatsAppTemplate(d);
  const edited = !fixed && (edit.subject !== (d.subject ?? "") || edit.body !== d.body);
  const who = draftWho(d);
  const card = useMemo(() => researchOf(d), [d]);
  const failed = failedChecks(card);
  const showResearch = more && hasResearch(d, card);
  return (
    <li
      className={`review-card${current ? " is-current" : ""}${decision ? ` is-${decision.kind}` : ""}${showResearch ? "" : " is-solo"}`}
      data-draft={d.id}
      aria-current={current ? "true" : undefined}
      onFocusCapture={onFocus}
    >
      <div className="review-draft">
        <div className="draft-head">
          <span id={`${id}-to`} className="draft-to">
            <strong>{who}</strong>
            {d.person?.email && <span className="muted small">{d.person.email}</span>}
          </span>
          <span className="muted small" title={`Step ${d.step_id}`}>
            {sendsText(d)}
            {edited ? <strong className="edited"> Edited</strong> : null}
          </span>
          {onClose && <button type="button" className="btn btn-quiet btn-small" onClick={onClose} aria-label={`Close the message to ${who}`}>Close</button>}
        </div>
        {why}
        {editing && !decision && !fixed ? (
          <>
            {d.channel === "email" && (
              <label className="field" htmlFor={`${id}-subject`}>
                <span className="field-label">Subject</span>
                <input id={`${id}-subject`} value={edit.subject} onChange={(e) => onEdit({ ...edit, subject: e.target.value })} />
              </label>
            )}
            <label className="field" htmlFor={`${id}-body`}>
              <span className="field-label">Message</span>
              <textarea
                id={`${id}-body`}
                className="autogrow"
                rows={Math.max(5, edit.body.split("\n").length + 2)}
                value={edit.body}
                onChange={(e) => onEdit({ ...edit, body: e.target.value })}
              />
            </label>
          </>
        ) : (
          <div className="review-message">
            {d.channel === "email" && <p className="review-subject">{edit.subject || <span className="muted">(no subject)</span>}</p>}
            <div className="review-body">{edit.body}</div>
          </div>
        )}
        {card?.evidence && <Evidence e={card.evidence} />}
        {d.channel_allowed === false && (
          <p className="notice notice-bad" role="alert">
            Your country rules no longer allow {humanise(d.channel).toLowerCase()} to this contact. Reject this message.
          </p>
        )}
        {!more && failed.length > 0 && (
          <p className="small research-failed-line">
            <DangerIcon /> {failed.length} {failed.length === 1 ? "check" : "checks"} to look at before you approve: open More.
          </p>
        )}
        {d.asset && <AssetPanel a={d.asset} />}
        {/* F-06 the draft coach: the score, and with More its reasons and one-click rewrites */}
        {!decision && <DraftCoach item={d} detail={more} />}
        {more && !showResearch && ruleText(d) && <p className="muted small">{ruleText(d)}.</p>}
        {decision ? (
          <p className={`review-verdict small${decision.kind === "rejected" ? " muted" : ""}`} role="status">
            {decision.kind === "approved" ? `Approved${decision.edited ? " with your edits" : ""}.` : `Rejected: ${reasonLabel(decision.reason).toLowerCase()}.`}
          </p>
        ) : rejecting ? (
          <div className="reject-reasons" role="group" aria-label={`Why reject the message to ${who}?`}>
            <label className="field" htmlFor={`${id}-note`}>
              <span className="field-label">A note for the outreach agent (optional)</span>
              <input id={`${id}-note`} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="reason-chips">
              {REJECT_REASONS.map((r) => (
                <button key={r.value} type="button" className="btn reason-chip" disabled={busy} onClick={() => onReject(r.value, note.trim())}>
                  {r.label}
                </button>
              ))}
              <button type="button" className="btn btn-small reason-cancel" onClick={onStartReject}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="review-actions" role="group" aria-labelledby={`${id}-to`}>
            <button type="button" className={`btn${primary || edited ? " btn-primary" : ""}`} disabled={busy} onClick={onApprove} aria-keyshortcuts="a">
              {edited ? "Approve with edits" : "Approve"}
            </button>
            {fixed ? (
              <span className="muted small">WhatsApp templates can't be edited: approve or reject.</span>
            ) : (
              <button type="button" className="link-btn" disabled={busy} onClick={onToggleEdit} aria-pressed={editing} aria-keyshortcuts="e">
                {editing ? "Done editing" : "Edit"}
              </button>
            )}
            {onDelay && (
              <button type="button" className="link-btn" disabled={busy} onClick={onDelay} aria-label={`Delay ${who}: back to Lined up`}>
                Delay
              </button>
            )}
            <button type="button" className="link-btn" disabled={busy} onClick={onStartReject} aria-keyshortcuts="r">
              Reject
            </button>
            <button type="button" className="link-btn" aria-expanded={more} onClick={() => setMore(!more)} aria-label={`${more ? "Less" : "More"} about the message to ${who}`}>
              {more ? "Less" : "More"}
            </button>
          </div>
        )}
        {!decision && like.eligible && (
          <p className="approve-like small">
            You approved {like.approved} like this ({like.label}).{" "}
            <button type="button" className="btn btn-small" disabled={busy} onClick={onApproveLike}>
              Approve all like this ({like.ids.length})
            </button>
          </p>
        )}
      </div>
      {showResearch && <Research card={card} d={d} open={researchOpen} onToggle={onToggleResearch} />}
    </li>
  );
}

export function OutboxBatch({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const batchId = String(task.payload.batch_id ?? "");
  const drafts = useAsync(() => crm.reviewBatch(batchId), [crm, batchId]);
  return (
    <Async
      state={drafts}
      label="Loading the messages"
      empty={(d) =>
        d.length === 0 ? (
          <Empty title="Nothing left to decide in this batch">
            <p>Every message has been approved, rejected or withdrawn.</p>
          </Empty>
        ) : null
      }
    >
      {(d) => <BatchEditor key={d.map((x) => x.id).join(",")} crm={crm} drafts={d} onDecided={drafts.reload} onDone={onDone} />}
    </Async>
  );
}

/**
 * The review itself. Decisions are saved one card at a time (or one group with "Approve all like this"), and the
 * card stays in place, marked, so the count of reviewed drafts holds. When every draft is decided the task closes.
 * "Approve all N" at the top approves every draft left that may go (none the country rules now forbid, none with a
 * failed check); those it leaves are said, to approve one by one. "Approve all like this" stays for a Find batch.
 */
export function BatchEditor<D extends ReviewDraft = ReviewDraft>({
  crm,
  drafts: given,
  onDone,
  why,
  onDelay,
  compact = false,
}: {
  crm: Crm;
  drafts: D[];
  onDecided?: () => void;
  onDone: () => void;
  /** K-12 a short "why this firm, why this message" for each draft. */
  why?: (d: D) => ReactNode;
  /** K-12 Delay a draft (the firm back to the Long list); the card leaves the batch once it resolves. Resolves to the
   *  words to show. */
  onDelay?: (d: D) => Promise<string>;
  /** One row a draft, the whole message opening in place on a click (Campaigns › To approve). */
  compact?: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [gone, setGone] = useState<ReadonlySet<string>>(() => new Set());
  const drafts = useMemo(() => given.filter((d) => !gone.has(d.id)), [given, gone]);
  const initial = useMemo(() => Object.fromEntries(given.map((d) => [d.id, { subject: d.subject ?? "", body: d.body }])), [given]);
  const [edits, setEdits] = useState<Record<string, Edit>>(initial);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [current, setCurrent] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [researchOpen, setResearchOpen] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const list = useRef<HTMLUListElement>(null);
  const left = drafts.filter((d) => !decisions[d.id]);
  const sweep = approvableAll(drafts, decisions);
  const held = left.length - sweep.length;
  const bulk = sweep.length > 1;

  const focusCard = useCallback((i: number) => {
    setCurrent(i);
    const el = list.current?.querySelector<HTMLElement>(`[data-draft="${drafts[i]?.id}"]`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [drafts]);

  const decide = async (ids: string[], approve: boolean, reason?: RejectReason, note?: string) => {
    if (!ids.length) return;
    setBusy(true);
    setMsg(null);
    try {
      const sent = approve ? buildEdits(drafts, edits, ids) : {};
      const n = await crm.decideDrafts(ids, approve, note || undefined, sent, approve ? undefined : reason);
      const next = { ...decisions };
      for (const id of ids) next[id] = approve ? { kind: "approved", edited: Boolean(sent[id]) } : { kind: "rejected", reason: reason ?? "other" };
      setDecisions(next);
      setEditing(null);
      setRejecting(null);
      const remaining = drafts.filter((d) => !next[d.id]).length;
      setMsg({ text: `${approve ? "Approved" : "Rejected"} ${n} ${n === 1 ? "message" : "messages"}.${remaining ? ` ${remaining} still to decide.` : ""}`, tone: "ok" });
      if (remaining === 0) onDone();
      else {
        const at = drafts.findIndex((d) => d.id === ids[ids.length - 1]);
        const j = nextOpen(drafts, next, at < 0 ? current : at);
        if (j >= 0) focusCard(j);
      }
    } catch (e) {
      setMsg({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(false);
    }
  };

  const delay = async (d: D) => {
    if (!onDelay) return;
    setBusy(true);
    setMsg(null);
    try {
      const text = await onDelay(d);
      const next = new Set(gone).add(d.id);
      setGone(next);
      setEditing(null);
      setRejecting(null);
      setCurrent(0);
      const remaining = given.filter((x) => !next.has(x.id) && !decisions[x.id]).length;
      setMsg({ text, tone: "ok" });
      if (remaining === 0) onDone();
    } catch (e) {
      setMsg({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(false);
    }
  };

  // Desktop shortcuts on the current card.
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyRef.current = (e: KeyboardEvent) => {
    if (busy || document.querySelector(".scrim")) return;
    const action = shortcutFor(e);
    if (!action) return;
    const d = drafts[current];
    if (!d) return;
    e.preventDefault();
    if (action === "next") return focusCard(Math.min(drafts.length - 1, current + 1));
    if (action === "prev") return focusCard(Math.max(0, current - 1));
    if (decisions[d.id]) return;
    if (action === "approve") void decide([d.id], true);
    if (action === "edit" && !isWhatsAppTemplate(d)) {
      setEditing(d.id);
      setTimeout(() => list.current?.querySelector<HTMLTextAreaElement>(`[data-draft="${d.id}"] textarea`)?.focus(), 0);
    }
    if (action === "reject") setRejecting(d.id);
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const done = drafts.length - left.length;
  return (
    <div className="batch review">
      <div className="review-bar">
        <span className="review-progress" role="status" aria-live="polite">
          <strong>{progressText(drafts, decisions)}</strong>
          <span className="review-meter" aria-hidden="true">
            <span style={{ width: `${drafts.length ? (100 * done) / drafts.length : 0}%` }} />
          </span>
        </span>
        {bulk && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void decide(sweep.map((d) => d.id), true)} title="Keys on a card: a approve · e edit · r reject · j next · k back">
            {busy ? "Saving…" : `Approve all ${sweep.length}`}
          </button>
        )}
      </div>
      {bulk && held > 0 && (
        <p className="small muted">
          {held} {held === 1 ? "message needs" : "messages need"} a look first, so Approve all leaves {held === 1 ? "it" : "them"} for you to approve one by one.
        </p>
      )}
      <ul className="review-cards" ref={list}>
        {drafts.map((d, i) => compact && open !== d.id ? (
          <ReviewRow key={d.id} d={d} edit={edits[d.id]!} decision={decisions[d.id]} busy={busy} onOpen={() => { setOpen(d.id); setCurrent(i); }} onApprove={() => void decide([d.id], true)} />
        ) : (
          <ReviewCard
            key={d.id}
            d={d}
            edit={edits[d.id]!}
            decision={decisions[d.id]}
            current={i === current}
            editing={editing === d.id}
            rejecting={rejecting === d.id}
            busy={busy}
            primary={!bulk && i === current}
            researchOpen={researchOpen[d.id] ?? true}
            like={approveAllLike(drafts, decisions, d)}
            onFocus={() => setCurrent(i)}
            onEdit={(e) => setEdits({ ...edits, [d.id]: e })}
            onToggleEdit={() => setEditing(editing === d.id ? null : d.id)}
            onApprove={() => void decide([d.id], true)}
            onStartReject={() => setRejecting(rejecting === d.id ? null : d.id)}
            onReject={(reason, note) => void decide([d.id], false, reason, note)}
            onApproveLike={() => {
              const like = approveAllLike(drafts, decisions, d);
              void decide(like.ids, true);
            }}
            onToggleResearch={() => setResearchOpen({ ...researchOpen, [d.id]: !(researchOpen[d.id] ?? true) })}
            why={why?.(d)}
            onDelay={onDelay && !decisions[d.id] ? () => void delay(d) : undefined}
            onClose={compact ? () => setOpen(null) : undefined}
          />
        ))}
      </ul>
      <Notice text={msg?.text ?? null} tone={msg?.tone} />
    </div>
  );
}

